import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    AlertTriangle,
    Check,
    Loader,
    ShieldAlert,
    SquareTerminal,
    Trash2,
    Upload,
    X,
} from 'lucide-react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';

import { apiManager } from './apiManager';

const ANSI_BY_LEVEL = {
    info: '\u001b[36m',
    warning: '\u001b[33m',
    error: '\u001b[31m',
    success: '\u001b[32m',
};

function writeSystemLine(terminal, message, level = 'info') {
    if (!terminal || !message) {
        return;
    }

    const color = ANSI_BY_LEVEL[level] || ANSI_BY_LEVEL.info;
    terminal.writeln(`${color}${message}\u001b[0m`);
}

const TerminalPanelV2 = ({ isOpen, onClose, workspaceId, initialPath, onWorkspaceChanged }) => {
    const [sessionId, setSessionId] = useState(null);
    const [sessionState, setSessionState] = useState(null);
    const [pendingApproval, setPendingApproval] = useState(null);
    const [isStarting, setIsStarting] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const terminalHostRef = useRef(null);
    const terminalRef = useRef(null);
    const fitAddonRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const streamAbortRef = useRef(null);
    const sessionIdRef = useRef(null);
    const isClosingRef = useRef(false);

    useEffect(() => {
        sessionIdRef.current = sessionId;
    }, [sessionId]);

    const cleanupLocalTerminal = () => {
        resizeObserverRef.current?.disconnect?.();
        resizeObserverRef.current = null;
        streamAbortRef.current?.abort?.();
        streamAbortRef.current = null;
        terminalRef.current?.dispose?.();
        terminalRef.current = null;
        fitAddonRef.current = null;
    };

    const endRemoteSession = async () => {
        const activeSessionId = sessionIdRef.current;
        if (!activeSessionId) {
            return;
        }

        try {
            await apiManager.killTerminalV2Session(activeSessionId);
        } catch {
            // Ignore shutdown errors during modal close.
        }
    };

    const handleRequestClose = async () => {
        if (isClosingRef.current) {
            return;
        }

        isClosingRef.current = true;
        cleanupLocalTerminal();
        await endRemoteSession();
        setSessionId(null);
        setSessionState(null);
        setPendingApproval(null);
        setErrorMessage(null);
        isClosingRef.current = false;
        onClose();
    };

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                void handleRequestClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !terminalHostRef.current || terminalRef.current) {
            return undefined;
        }

        const terminal = new Terminal({
            convertEol: true,
            cursorBlink: true,
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            fontSize: 13,
            theme: {
                background: '#08131f',
                foreground: '#d6e4f0',
                cursor: '#f6c453',
                selectionBackground: 'rgba(111, 162, 191, 0.35)',
            },
        });
        const fitAddon = new FitAddon();
        terminal.loadAddon(fitAddon);
        terminal.open(terminalHostRef.current);
        fitAddon.fit();
        terminal.focus();

        const resizeTerminal = () => {
            try {
                fitAddon.fit();
                if (sessionIdRef.current) {
                    void apiManager.resizeTerminalV2Session(sessionIdRef.current, terminal.cols, terminal.rows);
                }
            } catch {
                // Ignore layout race conditions during mount and close.
            }
        };

        const observer = new ResizeObserver(() => resizeTerminal());
        observer.observe(terminalHostRef.current);

        const dataDisposable = terminal.onData((data) => {
            if (!sessionIdRef.current) {
                return;
            }

            apiManager.sendTerminalV2Input(sessionIdRef.current, data).catch((error) => {
                writeSystemLine(terminal, error.message || 'Failed to send terminal input.', 'error');
            });
        });

        terminalRef.current = terminal;
        fitAddonRef.current = fitAddon;
        resizeObserverRef.current = observer;

        return () => {
            dataDisposable.dispose();
            observer.disconnect();
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !terminalRef.current || !workspaceId) {
            return undefined;
        }

        let cancelled = false;
        const abortController = new AbortController();
        streamAbortRef.current = abortController;

        const startTerminalSession = async () => {
            setIsStarting(true);
            setErrorMessage(null);
            setPendingApproval(null);
            terminalRef.current.clear();
            writeSystemLine(terminalRef.current, 'Starting Terminal V2 sandbox...', 'info');

            try {
                const sessionCreation = await apiManager.createTerminalV2Session(workspaceId, {
                    initialPath: initialPath || '',
                    sessionKind: 'human',
                    cols: terminalRef.current.cols,
                    rows: terminalRef.current.rows,
                });

                if (cancelled) {
                    return;
                }

                const nextSessionId = sessionCreation?.session?.id;
                setSessionId(nextSessionId);
                setSessionState(sessionCreation?.session || null);

                await apiManager.subscribeToTerminalV2Session(nextSessionId, (event) => {
                    if (cancelled || !terminalRef.current) {
                        return;
                    }

                    if (event.type === 'output') {
                        terminalRef.current.write(event.data || '');
                        return;
                    }

                    if (event.type === 'system') {
                        writeSystemLine(terminalRef.current, event.message, event.level || 'info');
                        return;
                    }

                    if (event.type === 'approval_required') {
                        setPendingApproval(event.approval || null);
                        writeSystemLine(terminalRef.current, event.approval?.summary || 'Approval required.', 'warning');
                        return;
                    }

                    if (event.type === 'approval_cleared') {
                        setPendingApproval(null);
                        return;
                    }

                    if (event.type === 'session_state') {
                        setSessionState(event.session || null);
                        setPendingApproval(event.session?.pendingApproval || null);
                        return;
                    }

                    if (event.type === 'session_end') {
                        writeSystemLine(terminalRef.current, 'Session ended.', 'warning');
                    }
                }, abortController.signal);
            } catch (error) {
                if (cancelled || abortController.signal.aborted) {
                    return;
                }

                setErrorMessage(error.message || 'Failed to start Terminal V2.');
                writeSystemLine(terminalRef.current, error.message || 'Failed to start Terminal V2.', 'error');
            } finally {
                if (!cancelled) {
                    setIsStarting(false);
                }
            }
        };

        void startTerminalSession();

        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [isOpen, workspaceId, initialPath]);

    useEffect(() => {
        if (isOpen) {
            requestAnimationFrame(() => {
                fitAddonRef.current?.fit?.();
                terminalRef.current?.focus?.();
            });
            return undefined;
        }

        cleanupLocalTerminal();
        setSessionId(null);
        setSessionState(null);
        setPendingApproval(null);
        setErrorMessage(null);
        return undefined;
    }, [isOpen]);

    const handleClear = () => {
        terminalRef.current?.clear();
    };

    const handleInterrupt = async () => {
        if (!sessionIdRef.current) {
            return;
        }

        try {
            await apiManager.signalTerminalV2Session(sessionIdRef.current, 'SIGINT');
        } catch (error) {
            writeSystemLine(terminalRef.current, error.message || 'Failed to interrupt session.', 'error');
        }
    };

    const handleRequestExport = async () => {
        if (!sessionIdRef.current) {
            return;
        }

        setIsExporting(true);
        try {
            const result = await apiManager.requestTerminalV2Export(sessionIdRef.current);
            setSessionState(result?.session || null);
            setPendingApproval(result?.pendingApproval || result?.session?.pendingApproval || null);

            if (!result?.approvalRequired) {
                writeSystemLine(terminalRef.current, 'No exportable changes were found in the sandbox.', 'info');
            }
        } catch (error) {
            writeSystemLine(terminalRef.current, error.message || 'Failed to request export.', 'error');
        } finally {
            setIsExporting(false);
        }
    };

    const handleApprove = async () => {
        if (!sessionIdRef.current) {
            return;
        }

        setIsApproving(true);
        try {
            const result = await apiManager.approveTerminalV2PendingAction(sessionIdRef.current);
            setSessionState(result?.session || null);
            setPendingApproval(result?.session?.pendingApproval || null);

            if (result?.actionType === 'export' && result?.appliedExport) {
                writeSystemLine(
                    terminalRef.current,
                    `Applied ${result.appliedExport.appliedChangeCount} change(s) back to the workspace.`,
                    'success',
                );
                onWorkspaceChanged?.();
                return;
            }

            writeSystemLine(terminalRef.current, 'Approval accepted.', 'success');
        } catch (error) {
            writeSystemLine(terminalRef.current, error.message || 'Failed to approve pending action.', 'error');
        } finally {
            setIsApproving(false);
        }
    };

    const handleDeny = async () => {
        if (!sessionIdRef.current) {
            return;
        }

        setIsApproving(true);
        try {
            const result = await apiManager.denyTerminalV2PendingAction(sessionIdRef.current);
            setSessionState(result?.session || null);
            setPendingApproval(null);
            writeSystemLine(terminalRef.current, 'Approval denied.', 'info');
        } catch (error) {
            writeSystemLine(terminalRef.current, error.message || 'Failed to deny pending action.', 'error');
        } finally {
            setIsApproving(false);
        }
    };

    if (typeof document === 'undefined') {
        return null;
    }

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-[rgba(var(--background-rgb),0.55)] backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        onMouseDown={(event) => event.stopPropagation()}
                        className="relative w-full max-w-6xl h-[78vh] bg-[rgba(var(--background-rgb),0.88)] border border-border-color rounded-xl shadow-2xl flex flex-col overflow-hidden"
                    >
                        <div className="flex-shrink-0 min-h-[48px] bg-surface border-b border-border-color flex items-center justify-between px-4 py-2">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="h-8 w-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
                                    <SquareTerminal size={16} />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-on-surface">Terminal V2 Sandbox</div>
                                    <div className="text-[11px] text-on-surface-dim truncate">
                                        {sessionState?.status === 'ACTIVE' ? 'Live disposable sandbox' : 'Starting disposable sandbox'}
                                        {sessionId ? ` · ${sessionId.slice(0, 8)}` : ''}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleInterrupt}
                                    className="px-2.5 py-1.5 rounded-md text-xs font-medium text-on-surface-dim hover:text-on-surface hover:bg-surface-dark transition-colors"
                                    title="Send Ctrl+C"
                                    disabled={!sessionId}
                                >
                                    Ctrl+C
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRequestExport}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-on-surface-dim hover:text-on-surface hover:bg-surface-dark transition-colors disabled:opacity-50"
                                    disabled={!sessionId || isExporting || Boolean(pendingApproval)}
                                >
                                    {isExporting ? <Loader size={13} className="animate-spin" /> : <Upload size={13} />}
                                    <span>Export Changes</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="p-1.5 rounded-md text-on-surface-dim hover:bg-surface-dark hover:text-on-surface transition-colors"
                                    title="Clear Terminal"
                                >
                                    <Trash2 size={16} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { void handleRequestClose(); }}
                                    className="p-1.5 rounded-md text-on-surface-dim hover:bg-surface-dark hover:text-on-surface transition-colors"
                                    title="Close Terminal"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        {pendingApproval ? (
                            <div className="flex-shrink-0 border-b border-amber-500/20 bg-amber-500/10 px-4 py-3 flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3 min-w-0">
                                    <ShieldAlert size={16} className="mt-0.5 text-amber-300 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-amber-100">Approval required</div>
                                        <div className="text-xs text-amber-100/80 mt-1">{pendingApproval.summary}</div>
                                        {pendingApproval.reason ? (
                                            <div className="text-[11px] text-amber-100/70 mt-1">{pendingApproval.reason}</div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        type="button"
                                        onClick={handleApprove}
                                        disabled={isApproving}
                                        className="inline-flex items-center gap-1.5 rounded-md bg-amber-300/20 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-300/30 transition-colors disabled:opacity-50"
                                    >
                                        {isApproving ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
                                        <span>Approve</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleDeny}
                                        disabled={isApproving}
                                        className="rounded-md border border-amber-300/30 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-300/10 transition-colors disabled:opacity-50"
                                    >
                                        Deny
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        {errorMessage ? (
                            <div className="flex-shrink-0 border-b border-red-500/20 bg-red-500/10 px-4 py-3 flex items-start gap-3">
                                <AlertTriangle size={16} className="mt-0.5 text-red-300 flex-shrink-0" />
                                <div className="text-xs text-red-100">{errorMessage}</div>
                            </div>
                        ) : null}

                        <div className="flex-1 min-h-0 bg-[#08131f]">
                            <div ref={terminalHostRef} className="h-full w-full p-3" />
                        </div>

                        <div className="flex-shrink-0 border-t border-border-color bg-surface px-4 py-2 flex items-center justify-between gap-4 text-[11px] text-on-surface-dim">
                            <div className="flex items-center gap-2 min-w-0">
                                {isStarting ? <Loader size={12} className="animate-spin" /> : <ShieldAlert size={12} />}
                                <span className="truncate">Reads and in-box writes are allowed. Exports and outbound sends require approval.</span>
                            </div>
                            <div className="shrink-0">{sessionState?.lastExportSummary?.totalChangeCount ? `${sessionState.lastExportSummary.totalChangeCount} pending change(s)` : 'Disposable session'}</div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body,
    );
};

export default TerminalPanelV2;